import { jsx, jsxs } from 'react/jsx-runtime';
import { useState, useMemo } from 'react';
import cns from '../../../../node_modules/classnames/index.js';
import Button from '../../Button/Button.js';
import { NOOP } from '../../../../utils/const.js';
import DontShowAgain from '../../../DontShowAgain/DontShowAgain.js';
import useDontShowAgainMessageRead from '../../../DontShowAgain/useDontShowAgainMessageRead.js';
import Body from './Body.js';
import Item from './Item.js';
import { createClosedByDontShowAgainEvent } from './utils.js';
import useModalContext from './useModalContext.js';

function createWrappedAsyncCallback(func, beforeCallback, afterCallback) {
    return () => {
        if (func) {
            const result = func();
            if (result instanceof Promise) {
                beforeCallback();
                return result.then((res) => {
                    afterCallback();
                    return res;
                }, (err) => {
                    afterCallback();
                    throw err;
                });
            }
        }
        return undefined;
    };
}
/**
Un item di tipo Confirm di `Modal`. Per avvertire l'utente che quello che sta per fare è irreversibile.

_Quando si utilizza?_

- Usare le confirm solo quando è strettamente necessario e non per azioni di routine.
- Essere molto specifici (vedi sezione Testi) ed informare gli utenti delle conseguenze che stanno per avvenire,
facendo riconoscere il possibile errore in maniera semplice.
- Usare opzioni di risposta (testi dei pulsanti) parlanti, che sintetizzino bene le conseguenze ed evitare risposte Sì/No.
Ad es. in caso di eliminazione di un file scrivere "Elimina il file" "Mantieni il file"
- Essendo che l'utente deve controllare e ricontrollare quello che sta facendo e
che bisogna evitare che compia delle azioni automatiche (come cliccare subito sul pulsante a enfasi alta)
dobbiamo creare delle situazioni diverse da quelle a cui è abituato.
- Prevedere una checkbox "Non mostrare più questo messaggio" nel caso di confirm create per educare l'utente all'utilizzo di nuove
feature del software con effetti indesiderati. In questo caso la confirm sarà mostrata per un periodo di tempo prestabilito.

*/
function ConfirmItem({ onPrimaryClick = NOOP, onSecondaryClick = NOOP, onClose = NOOP, className = undefined, classNameBody = undefined, dontShowAgainId = undefined, dontShowAgainMessage = 'Non mostrare più questo messaggio', image = undefined, keepAliveWhenHidden = true, id, title, message, primaryLabel, secondaryLabel, }) {
    const messageRead = useDontShowAgainMessageRead(dontShowAgainId, true);
    const { dragging, onMouseDown } = useModalContext();
    const [working, setWorking] = useState(false);
    const [primaryCallback, secondaryCallback] = useMemo(() => {
        const startWorking = () => setWorking(true);
        const stopWorking = () => setWorking(false);
        const primaryCB = createWrappedAsyncCallback(onPrimaryClick, startWorking, stopWorking);
        const secondaryCB = createWrappedAsyncCallback(onSecondaryClick, startWorking, stopWorking);
        return [primaryCB, secondaryCB];
    }, [onPrimaryClick, onSecondaryClick]);
    if (messageRead) {
        onClose(createClosedByDontShowAgainEvent(id, dontShowAgainId));
        return null;
    }
    return (jsx(Item, { id: id, className: className, keepAliveWhenHidden: keepAliveWhenHidden, children: jsx(Body, { className: classNameBody, children: jsx("div", { className: "v2-confirm-modal", children: jsxs("div", { className: "v2-confirm-modal-grid", children: [jsx("div", { className: "v2-confirm-modal-image", children: image }), jsxs("div", { className: "v2-confirm-modal-messages", children: [jsx("h1", { className: cns('can-grab', {
                                        grabbing: dragging,
                                    }), onMouseDown: onMouseDown, role: "presentation", children: title }), jsx("div", { children: message })] }), jsx("div", {}), ' ', jsxs("div", { className: "v2-confirm-modal-footer", children: [jsx(Button, { emphasis: Button.Emphasis.HIGH, disabled: working, primary: true, onClick: primaryCallback, children: primaryLabel }), jsx(Button, { emphasis: Button.Emphasis.HIGH, disabled: working, onClick: secondaryCallback, children: secondaryLabel })] }), dontShowAgainId && jsx("div", {}), ' ', dontShowAgainId && jsx(DontShowAgain, { className: "v2-confirm-modal__dontshowagain", id: dontShowAgainId, message: dontShowAgainMessage })] }) }) }) }));
}

export { ConfirmItem as default };
